import React, { useState } from 'react'
import { Activity, CheckCircle2, ChevronDown, ChevronUp, Clock3, Database, FileSearch, Loader2, ShieldCheck, Terminal, XCircle } from 'lucide-react'
import type { ToolEvent } from '../../hooks/useChatStream'
import { sanitizeToolPayload, toolOutcomeCopy, toolOutcomeStatus } from '../../lib/toolOutcomes'
import { compactTranscriptContent, shouldCollapseTranscriptContent } from '../../lib/transcript'

function ToolIcon({ name }: { name: string }) {
  if (name === 'authenticate_saviynt') return <ShieldCheck size={15} />
  if (name.includes('connection')) return <Database size={15} />
  if (name.includes('document') || name === 'web_search') return <FileSearch size={15} />
  if (name.includes('terraform') || name.includes('rollback')) return <Terminal size={15} />
  return <Activity size={15} />
}

function formatDuration(duration?: number) {
  if (duration === undefined) return null
  if (duration < 1000) return `${duration} ms`
  return `${(duration / 1000).toFixed(duration < 10000 ? 1 : 0)} s`
}

function CollapsiblePayload({ label, value }: { label: string; value: Record<string, unknown> }) {
  const [showFull, setShowFull] = useState(false)
  const content = JSON.stringify(sanitizeToolPayload(value), null, 2)
  const collapsible = shouldCollapseTranscriptContent(content)
  const visibleContent = collapsible && !showFull ? compactTranscriptContent(content) : content

  return (
    <details>
      <summary>{label}</summary>
      <pre>{visibleContent}{collapsible && !showFull ? '\n…' : ''}</pre>
      {collapsible && (
        <button type="button" className="long-content-toggle" onClick={() => setShowFull(value => !value)}>
          {showFull ? 'Show less' : 'Show full payload'}
        </button>
      )}
    </details>
  )
}

export default function ToolOutcomeCard({ tool, fallbackTimestamp, historical = false }: { tool: ToolEvent; fallbackTimestamp?: Date; historical?: boolean }) {
  const [expanded, setExpanded] = useState(false)
  const status = toolOutcomeStatus(tool, historical)
  const copy = toolOutcomeCopy(tool, historical)
  const timestamp = tool.started_at ? new Date(tool.started_at) : fallbackTimestamp
  const duration = formatDuration(tool.duration_ms)

  return (
    <article className={`tool-outcome-card ${status}`}>
      <button
        type="button"
        className="tool-outcome-summary"
        onClick={() => setExpanded(value => !value)}
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${copy.title} details`}
      >
        <span className="tool-outcome-icon">
          {status === 'pending' ? <Loader2 size={15} className="spin" /> : status === 'failed' ? <XCircle size={15} /> : <ToolIcon name={tool.name} />}
        </span>
        <span className="tool-outcome-copy">
          <strong>{copy.title}</strong>
          <span>{copy.resultMessage || copy.description}</span>
        </span>
        <span className="tool-outcome-meta">
          {(timestamp || duration) && <span><Clock3 size={10} />{duration || timestamp?.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>}
          {(status === 'success' || status === 'completed') && <CheckCircle2 size={13} />}
          {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </span>
      </button>

      {expanded && (
        <div className="tool-outcome-technical">
          <div>
            <span>Technical action</span>
            <code>{tool.name}</code>
          </div>
          {tool.input && (
            <CollapsiblePayload label="Sanitized input" value={tool.input} />
          )}
          {tool.result && (
            <CollapsiblePayload label="Sanitized result" value={tool.result} />
          )}
        </div>
      )}
    </article>
  )
}
